// src/views/PitaCukai/FormPelekatanPC.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  Input,
  DatePicker,
  Button,
  Typography,
  Space,
  message,
  Modal,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../configs"; // SESUAIKAN PATH
import Title from "antd/es/typography/Title";

const { Text } = Typography;
const { Option } = Select;

export default function FormEditPelekatanPC() {
  const [form] = Form.useForm();
  const { idPelekatanPc } = useParams(); // Sesuai route: /pita/editpelekatan/:idPelekatanPc
  const isViewExisting = !!idPelekatanPc;
  console.log("idPelekatanPc", idPelekatanPc);
  const [isEditing, setIsEditing] = useState(!isViewExisting);
  const [headerId, setHeaderId] = useState(null);

  const [importirOptions, setImportirOptions] = useState([]);
  const [loadingImportir, setLoadingImportir] = useState(false);

  const [allNopenOptions, setAllNopenOptions] = useState([]);
  const [loadingNopen, setLoadingNopen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const disabledAll = isViewExisting && !isEditing;

  // ====== LOAD LIST IMPORTIR (CONTACT) ======
  const fetchImportirList = async () => {
    setLoadingImportir(true);
    try {
      const res = await axiosInstance.get("/contact");
      const list = res.data || [];
      setImportirOptions(
        list
          .filter((c) => c.status === 1 && c.npwp)
          .map((c) => ({
            label: `${c.name} (${c.npwp})`,
            value: c.npwp,
          }))
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar importir");
    } finally {
      setLoadingImportir(false);
    }
  };

  useEffect(() => {
    fetchImportirList();
  }, []);

  // ====== LOAD LIST NOPEN BERDASARKAN IMPORTIR (id_customer = npwp) ======
  const fetchNopenList = async (idCustomerNpwp) => {
    if (!idCustomerNpwp) {
      setAllNopenOptions([]);
      return;
    }
    setLoadingNopen(true);
    try {
      const res = await axiosInstance.get("/incoming/getListNopenLekat", {
        params: { id_customer: idCustomerNpwp },
      });
      const list = res.data?.data || [];
      setAllNopenOptions(
        list.map((item) => ({
          label: `${item.document_number} - (${dayjs(
            item.document_date
          ).format("DD-MM-YYYY")})`,
          value: item.document_number,
        }))
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat daftar Nopen");
    } finally {
      setLoadingNopen(false);
    }
  };

  // ====== WATCH groups UNTUK FILTER NOPEN & HITUNG TOTAL ======
  const groupsWatch = Form.useWatch("groups", form) || [];

  const selectedNopens = useMemo(() => {
    const arr = [];
    groupsWatch.forEach((g) => {
      if (g?.nopen) arr.push(g.nopen);
    });
    return arr;
  }, [groupsWatch]);

  const totalKemasanDilekat = useMemo(() => {
    let tK = 0;
    groupsWatch.forEach((g) => {
      (g?.details || []).forEach((r) => {
        tK += Number(r?.jumlah_kemasan_lekat || 0);
      });
    });
    return tK;
  }, [groupsWatch]);

  // ====== ONCHANGE IMPORTIR ======
  const handleImportirChange = (npwp) => {
    form.setFieldsValue({ id_customer: npwp, groups: [] });
    setAllNopenOptions([]);
    fetchNopenList(npwp);
  };

  // ====== ONCHANGE NOPEN DI TIAP GROUP (AMBIL DETAIL DARI ITEM-STOCK) ======
  const handleChangeNopen = async (noDocument, groupIndex) => {
    if (!noDocument && noDocument !== "") return;

    setLoadingDetail(true);
    try {
      const res = await axiosInstance.get("/item-stock", {
        params: { nopen: noDocument },
      });

      const rows = res.data?.data || [];

      const mappedDetails = rows.map((d) => ({
        id_pelekatan_pc_detail: d.id_pelekatan_pc_detail || 0,
        nopen: d.nopen,
        seri: d.seri,
        item_code: d.item_code,
        nama_barang: d.item_name,
        isi_ml: d.isi_ml,
        jumlah_kemasan: d.jumlah_kemasan,
        kemasan_display: d.kemasan || "",
        lot_asal: d.lokasi_lot,
        kemasan: d.kemasan,
        kemasan_lekat: d.kemasan,
        jumlah_kemasan_lekat: undefined,
        lot_tujuan: "",
      }));

      const current = form.getFieldValue("groups") || [];
      current[groupIndex] = {
        ...(current[groupIndex] || {}),
        nopen: noDocument,
        details: mappedDetails,
      };
      form.setFieldsValue({ groups: current });
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil detail Nopen / stripping");
    } finally {
      setLoadingDetail(false);
    }
  };

  // ====== LOAD DETAIL EXISTING (MODE VIEW/EDIT) ======
  const fetchDetailPelekatan = async () => {
    if (!idPelekatanPc) return;
    setLoadingDetail(true);
    try {
      const res = await axiosInstance.get(
        `/pelekatan-pc/${idPelekatanPc}/with-details`
      );

      // Asumsi response: { header: {...}, details: [...] }
      const data = res.data || {};
      const header = data.header || data.data?.header || {};
      const details = data.details || data.data?.details || [];

      setHeaderId(header.id_pelekatan_pc || header.id_pelekatan_pc || null);

      // map header -> form
      form.setFieldsValue({
        no_ck1a: header.no_ck1a || "",
        tanggal_ck1a: header.tanggal_ck1a
          ? dayjs(header.tanggal_ck1a)
          : null,
        no_ba: header.no_ba || "",
        tanggal_ba: header.tanggal_ba ? dayjs(header.tanggal_ba) : null,
        id_customer: header.id_customer || "",
      });

      // load nopen list untuk importir ini
      if (header.id_customer) {
        await fetchNopenList(header.id_customer);
      }

      // group details by nopen
      const groupsMap = {};
      details.forEach((d) => {
        const key = d.nopen || "-";
        if (!groupsMap[key]) {
          groupsMap[key] = {
            nopen: key,
            details: [],
          };
        }
        groupsMap[key].details.push({
          id_pelekatan_pc_detail: d.id_pelekatan_pc_detail || 0,
          nopen: d.nopen,
          seri: d.seri,
          item_code: d.item_code,
          nama_barang:
            d.nama_barang || d.item_name || `${d.item_code || ""}`,
          jumlah_kemasan: d.jumlah_kemasan,
          kemasan_display: d.kemasan_lekat || "",
          lot_asal: d.lot_tujuan || "",
          kemasan: d.kemasan || "",
          kemasan_lekat: d.kemasan_lekat,
          jumlah_kemasan_lekat: d.jumlah_kemasan_lekat || 0,
          lot_tujuan: d.lot_tujuan || "",
        });
      });

      const groupsArr = Object.values(groupsMap);
      form.setFieldsValue({
        groups: groupsArr,
      });
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat detail pelekatan pita cukai");
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (isViewExisting) {
      fetchDetailPelekatan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPelekatanPc]);

  // ====== SUBMIT (CREATE / UPDATE) ======
  const handleFinish = async (values) => {
    const groups = values.groups || [];

    if (!groups.length) {
      message.error("Minimal harus ada satu Nopen & detail barang");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;
    const groups = values.groups || [];
    const nowIso = new Date().toISOString();

    const headerPayload = {
      id_pelekatan_pc: headerId || 0,
      id_customer: values.id_customer || 0,
      no_ba: values.no_ba,
      tanggal_ba: values.tanggal_ba
        ? dayjs(values.tanggal_ba).format("YYYY-MM-DD")
        : null,
      no_ck1a: values.no_ck1a,
      tanggal_ck1a: values.tanggal_ck1a
        ? dayjs(values.tanggal_ck1a).format("YYYY-MM-DD")
        : null,
      lampiran_1: null,
    };

    const detailsPayload = [];
    groups.forEach((g) => {
      const nopen = g.nopen || "";
      (g.details || []).forEach((row, idx) => {
        detailsPayload.push({
          id_pelekatan_pc_detail: row.id_pelekatan_pc_detail || 0,
          id_pelekatan_pc: headerId || 0,
          nopen,
          seri: row.seri || idx + 1,
          item_code: row.item_code,
          kemasan: row.kemasan || row.kemasan_lekat || "",
          jumlah_kemasan: Number(row.jumlah_kemasan) || 0,
          lot_asal: row.lot_asal || "",
          kemasan_lekat: row.kemasan_lekat || "",
          jumlah_kemasan_lekat: Number(row.jumlah_kemasan_lekat) || 0,
          lot_tujuan: row.lot_tujuan || "",
        });
      });
    });

    if (!detailsPayload.length) {
      message.error("Detail barang masih kosong");
      return;
    }

    const payload = {
      header: headerPayload,
      details: detailsPayload,
    };

    try {
      setSaving(true);
      if (headerId) {
        // UPDATE
        await axiosInstance.put(`/pelekatan-pc/${headerId}/with-details`, payload);
        message.success("Perubahan pelekatan pita cukai berhasil disimpan");
        setIsEditing(false);
        fetchDetailPelekatan();
      } else {
        // CREATE
        await axiosInstance.post("/pelekatan-pc", payload);
        message.success("Data pelekatan pita cukai berhasil disimpan");
        form.resetFields();
      }
      setConfirmVisible(false);
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan data pelekatan");
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    fetchDetailPelekatan();
  };

  const handleCancel = () => {
    window.history.back();
  };

  const submitLabel = isViewExisting ? "Simpan Perubahan" : "Simpan";

  return (
    <Card
      className="kt-portlet"
      bodyStyle={{ padding: 24 }}
      style={{
        borderRadius: 16,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        border: "1px solid #F3F6F9",
      }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            {isViewExisting ? "Detail Pelekatan Pita Cukai" : "Tambah Pelekatan Pita Cukai"}
          </Title>
          <Text type="secondary">
            {isViewExisting ? "Lihat dan ubah data pelekatan pita cukai." : "Input data pelekatan pita cukai baru."}
          </Text>
        </Col>
        <Col>
          <Space>
            <Button onClick={handleCancel}>Kembali</Button>
            {isViewExisting && !isEditing && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleStartEdit}
              >
                Edit
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          groups: [],
        }}
      >
        {/* ===== HEADER: CK1A & BA & IMPORTIR ===== */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No CK1A</Text>}
              name="no_ck1a"
              rules={[{ required: true, message: "Isi No CK1A" }]}
            >
              <Input
                placeholder="Masukkan No CK1A"
                disabled={disabledAll}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal CK1A</Text>}
              name="tanggal_ck1a"
              rules={[{ required: true, message: "Pilih tanggal CK1A" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder="Pilih tanggal"
                format="DD MMMM YYYY"
                disabled={disabledAll}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>BA Pelekatan</Text>}
              name="no_ba"
              rules={[{ required: true, message: "Isi nomor BA Pelekatan" }]}
            >
              <Input
                placeholder="Nomor BA Pelekatan"
                disabled={disabledAll}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal BA Pelekatan</Text>}
              name="tanggal_ba"
              rules={[{ required: true, message: "Pilih tanggal BA" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder="Pilih tanggal"
                format="DD MMMM YYYY"
                disabled={disabledAll}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Importir</Text>}
              name="id_customer"
              rules={[{ required: true, message: "Pilih Nama Importir" }]}
            >
              <Select
                placeholder="Pilih Nama Importir"
                loading={loadingImportir}
                options={importirOptions}
                showSearch
                optionFilterProp="label"
                onChange={handleImportirChange}
                disabled={disabledAll || isViewExisting} // importir fix saat edit
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ===== LIST GROUP NOPEN ===== */}
        <Form.List name="groups">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => {
                const groupIndex = field.name;
                const currentGroup = groupsWatch?.[groupIndex] || {};
                const currentNopen = currentGroup.nopen;

                const availableNopenOptions = allNopenOptions.filter((opt) => {
                  if (opt.value === currentNopen) return true;
                  return !selectedNopens.includes(opt.value);
                });

                return (
                  <Card
                    key={field.key}
                    bodyStyle={{ padding: 0 }}
                    style={{
                      marginTop: 16,
                      borderRadius: 4,
                      border: "1px solid #E4E6EF",
                      overflow: "hidden",
                    }}
                  >
                    {/* HEADER GROUP: NOPEN + HAPUS GROUP */}
                    <div
                      style={{
                        padding: "12px 16px",
                        background: "#F3F6F9",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ flex: 1, paddingRight: 16 }}>
                        <Form.Item
                          {...field}
                          name={[field.name, "nopen"]}
                          fieldKey={[field.fieldKey, "nopen"]}
                          label={
                            <Text strong>
                              Nomor Pendaftaran (NOPEN) - Group{" "}
                              {Number(groupIndex) + 1}
                            </Text>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Pilih Nopen untuk group ini",
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            placeholder="Pilih Nomor Pendaftaran"
                            showSearch
                            optionFilterProp="label"
                            loading={loadingNopen}
                            value={currentNopen}
                            onChange={(val) => {
                              if (disabledAll) return;
                              handleChangeNopen(val, groupIndex);
                            }}
                            disabled={disabledAll}
                          >
                            {availableNopenOptions.map((opt) => (
                              <Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>

                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => !disabledAll && remove(field.name)}
                        disabled={disabledAll}
                      >
                        Hapus Nopen
                      </Button>
                    </div>

                    {/* TABEL BARANG UNTUK GROUP INI */}
                    <div style={{ marginTop: 8 }}>
                      {/* header tabel */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "0.7fr 3fr 2.4fr 2fr 2.4fr 2.4fr 60px",
                          background: "#F3F6F9",
                          padding: "8px 16px",
                          fontWeight: 600,
                          fontSize: 13,
                          alignItems: "center",
                        }}
                      >
                        <div>SERI</div>
                        <div>NAMA BARANG</div>
                        <div>JUMLAH KEMASAN</div>
                        <div>LOT ASAL</div>
                        <div>KEMASAN DILEKAT</div>
                        <div>LOT TUJUAN</div>
                        <div style={{ textAlign: "center" }}>AKSI</div>
                      </div>

                      <Form.List name={[field.name, "details"]}>
                        {(
                          detailFields,
                          { add: addDetail, remove: removeDetail }
                        ) => (
                          <>
                            {detailFields.map((detailField, idx) => (
                              <div key={detailField.key}>
                                {/* hidden untuk ID detail */}
                                <Form.Item
                                  {...detailField}
                                  name={[
                                    detailField.name,
                                    "id_pelekatan_pc_detail",
                                  ]}
                                  fieldKey={[
                                    detailField.fieldKey,
                                    "id_pelekatan_pc_detail",
                                  ]}
                                  hidden
                                />

                                <Form.Item
                                  {...detailField}
                                  name={[detailField.name, "seri"]}
                                  fieldKey={[detailField.fieldKey, "seri"]}
                                  hidden
                                />
                                <Form.Item
                                  {...detailField}
                                  name={[detailField.name, "item_code"]}
                                  fieldKey={[detailField.fieldKey, "item_code"]}
                                  hidden
                                />
                                <Form.Item
                                  {...detailField}
                                  name={[detailField.name, "kemasan"]}
                                  fieldKey={[detailField.fieldKey, "kemasan"]}
                                  hidden
                                />

                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                      "0.7fr 3fr 2.4fr 2fr 2.4fr 2.4fr 60px",
                                    padding: "8px 16px",
                                    borderTop: "1px solid #E4E6EF",
                                    alignItems: "center",
                                  }}
                                >
                                  {/* SERI (display) */}
                                  <div style={{ paddingRight: 8 }}>
                                    {idx + 1}
                                  </div>

                                  {/* NAMA BARANG */}
                                  <Form.Item
                                    {...detailField}
                                    name={[detailField.name, "nama_barang"]}
                                    fieldKey={[
                                      detailField.fieldKey,
                                      "nama_barang",
                                    ]}
                                    style={{ marginBottom: 0, paddingRight: 8 }}
                                  >
                                    <Input
                                      placeholder="NAMA BARANG"
                                      disabled
                                    />
                                  </Form.Item>

                                  {/* JUMLAH KEMASAN */}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 4,
                                      paddingRight: 8,
                                    }}
                                  >
                                    <Form.Item
                                      {...detailField}
                                      name={[
                                        detailField.name,
                                        "jumlah_kemasan",
                                      ]}
                                      fieldKey={[
                                        detailField.fieldKey,
                                        "jumlah_kemasan",
                                      ]}
                                      style={{ marginBottom: 0, flex: 1 }}
                                    >
                                      <Input
                                        placeholder="Jlh Kemasan"
                                        disabled
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      {...detailField}
                                      name={[
                                        detailField.name,
                                        "kemasan_display",
                                      ]}
                                      style={{
                                        marginBottom: 0,
                                        width: 70,
                                      }}
                                    >
                                      <Input disabled />
                                    </Form.Item>
                                  </div>

                                  {/* LOT ASAL */}
                                  <Form.Item
                                    {...detailField}
                                    name={[detailField.name, "lot_asal"]}
                                    fieldKey={[
                                      detailField.fieldKey,
                                      "lot_asal",
                                    ]}
                                    style={{ marginBottom: 0, paddingRight: 8 }}
                                  >
                                    <Input
                                      placeholder="LOT ASAL"
                                      disabled
                                    />
                                  </Form.Item>

                                  {/* KEMASAN DILEKAT + QTY DILEKAT */}
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 4,
                                      paddingRight: 8,
                                    }}
                                  >
                                    <Form.Item
                                      {...detailField}
                                      name={[
                                        detailField.name,
                                        "jumlah_kemasan_lekat",
                                      ]}
                                      fieldKey={[
                                        detailField.fieldKey,
                                        "jumlah_kemasan_lekat",
                                      ]}
                                      style={{ marginBottom: 0, flex: 1 }}
                                    >
                                      <Input
                                        placeholder="Jumlah"
                                        inputMode="numeric"
                                        disabled={disabledAll}
                                      />
                                    </Form.Item>
                                    <Form.Item
                                      {...detailField}
                                      name={[detailField.name, "kemasan_lekat"]}
                                      fieldKey={[
                                        detailField.fieldKey,
                                        "kemasan_lekat",
                                      ]}
                                      style={{ marginBottom: 0, width: 70 }}
                                    >
                                      <Input
                                        placeholder="Kemasan"
                                        disabled={disabledAll}
                                      />
                                    </Form.Item>
                                  </div>

                                  {/* LOT TUJUAN */}
                                  <Form.Item
                                    {...detailField}
                                    name={[detailField.name, "lot_tujuan"]}
                                    fieldKey={[detailField.fieldKey, "lot_tujuan"]}
                                    style={{ marginBottom: 0, paddingRight: 8 }}
                                    rules={[
                                      { required: true, message: "Lot wajib" },
                                      {
                                        pattern: /^[A-S]\.[1-3]\.\d{1,2}$/,
                                        message: "Format: Rak.Lantai.Kolom"
                                      }
                                    ]}
                                  >
                                    <Input
                                      placeholder="Rak.Lantai.Kolom"
                                      disabled={disabledAll}
                                      onBlur={(e) => {
                                        if (disabledAll) return;
                                        let raw = e.target.value.toUpperCase().trim().replace(/\s+/g, '');
                                        if (!raw) return;

                                        let formatted = raw;

                                        // Already valid: A.1.1
                                        if (/^[A-S]\.[1-3]\.\d{1,2}$/.test(formatted)) {
                                          // OK
                                        }
                                        // B23 -> B.2.3 (Row + Level + Col)
                                        else if (/^[A-S][1-3]\d{1,2}$/.test(formatted)) {
                                          const row = formatted[0];
                                          const level = formatted[1];
                                          const col = formatted.slice(2);
                                          formatted = `${row}.${level}.${col}`;
                                        }
                                        // A.23 -> A.2.3 (Row.Level+Col)
                                        else if (/^[A-S]\.[1-3]\d{1,2}$/.test(formatted)) {
                                          const row = formatted[0];
                                          const level = formatted[2];
                                          const col = formatted.slice(3);
                                          formatted = `${row}.${level}.${col}`;
                                        }
                                        // A2.3 -> A.2.3 (Row+Level.Col)
                                        else if (/^[A-S][1-3]\.\d{1,2}$/.test(formatted)) {
                                          formatted = formatted[0] + '.' + formatted.slice(1);
                                        }

                                        // Update field value
                                        const groups = form.getFieldValue("groups") || [];
                                        if (groups[field.name]?.details?.[detailField.name]) {
                                          groups[field.name].details[detailField.name].lot_tujuan = formatted;
                                          form.setFieldsValue({ groups });
                                          setTimeout(() => {
                                            form.validateFields([["groups", field.name, "details", detailField.name, "lot_tujuan"]]);
                                          }, 0);
                                        }
                                      }}
                                    />
                                  </Form.Item>

                                  {/* HAPUS BARIS BARANG */}
                                  <div style={{ textAlign: "center" }}>
                                    <Button
                                      type="text"
                                      icon={
                                        <DeleteOutlined
                                          style={{ color: "#F64E60" }}
                                        />
                                      }
                                      onClick={() =>
                                        !disabledAll &&
                                        removeDetail(detailField.name)
                                      }
                                      disabled={disabledAll}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </Form.List>
                    </div>
                  </Card>
                );
              })}

              {/* tombol tambah Nopen group */}
              <div style={{ marginTop: 16 }}>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    !disabledAll &&
                    add({
                      nopen: null,
                      details: [],
                    })
                  }
                  disabled={disabledAll}
                >
                  Tambah Nopen
                </Button>
              </div>
            </>
          )}
        </Form.List>

        {/* ===== TOTALS ===== */}
        <Row style={{ marginTop: 16 }}>
          <Col xs={24} md={6}>
            <Text strong>TOTAL KEMASAN DILEKAT</Text>
            <Input
              value={totalKemasanDilekat}
              disabled
              style={{ marginTop: 4 }}
            />
          </Col>
        </Row>

        {/* ===== ACTION BUTTONS ===== */}
        {/* ===== ACTION BUTTONS ===== */}
        <Row justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Button onClick={handleCancel}>Kembali</Button>

            {isEditing && (
              <>
                {isViewExisting && (
                  <Button
                    icon={<RollbackOutlined />}
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    Batal Edit
                  </Button>
                )}
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  icon={<SaveOutlined />}
                  style={{
                    background: "#1BC5BD",
                    borderColor: "#1BC5BD",
                    minWidth: 140,
                    fontWeight: 600,
                  }}
                >
                  {submitLabel}
                </Button>
              </>
            )}
          </Space>
        </Row>
      </Form>


      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Perubahan Pelekatan"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan Perubahan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        {pendingValues && (
          <div>
            <p>Apakah Anda yakin ingin menyimpan perubahan data pelekatan ini?</p>
            <div style={{ background: "#f5f5f5", padding: 10, borderRadius: 4, marginBottom: 16 }}>
              <p style={{ margin: 0 }}>
                <strong>No CK1A:</strong> {pendingValues.no_ck1a}
              </p>
              <p style={{ margin: 0 }}>
                <strong>No BA:</strong> {pendingValues.no_ba}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Importir:</strong> {importirOptions.find(o => o.value === pendingValues.id_customer)?.label || pendingValues.id_customer}
              </p>
            </div>

            <Text strong>Ringkasan Barang:</Text>
            <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0" }}>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead style={{ background: "#fafafa", position: "sticky", top: 0 }}>
                  <tr>
                    <th style={{ padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>Nopen</th>
                    <th style={{ padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>Nama Barang</th>
                    <th style={{ padding: "4px 8px", textAlign: "right", borderBottom: "1px solid #f0f0f0" }}>Jml Dilekat</th>
                  </tr>
                </thead>
                <tbody>
                  {(pendingValues.groups || []).flatMap(g =>
                    (g.details || []).map((d, idx) => (
                      <tr key={`${g.nopen}-${idx}`} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "4px 8px" }}>{g.nopen}</td>
                        <td style={{ padding: "4px 8px" }}>{d.nama_barang}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>{d.jumlah_kemasan_lekat}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </Card >
  );
}
